import type { ComputedRef } from 'vue';

import { useFeatureFlags } from '~/composables/useFeatureFlags';

/** Where the server's answer is kept in the payload; `serverFlagAnswer` reads it from route middleware. */
export const serverFlagKey = (flagName: string) => `feature-flag:${flagName}`;

/**
 * A feature flag answered once on the server and carried to the browser in the payload, for a
 * flag that decides what a page renders.
 *
 * The server and the browser each run their own flag provider, and the browser's falls back to
 * the static bootstrap when it cannot reach Unleash (a tracker blocker, a proxy, a network blip).
 * A flag read on both sides can then disagree, and the browser would hydrate the other page over
 * the server's HTML. Every caller reading the same flag shares the one answer.
 *
 * As a ref, for a caller that outlives a refresh of the answer: a layout that stays mounted
 * through an in-tab sign-in sees the answer asked again for the student. `useServerFlag` is the
 * answer as it stands.
 *
 * Call it before the caller's first await: it registers asyncData, which needs the Nuxt instance.
 */
export const useServerFlagRef = async (flagName: string): Promise<ComputedRef<boolean>> => {
  const { getFlag } = useFeatureFlags();
  const { data } = await useAsyncData(serverFlagKey(flagName), () => getFlag(flagName));

  return computed(() => data.value === true);
};

/** {@link useServerFlagRef}'s answer as it stands when the caller asks. */
export const useServerFlag = async (flagName: string): Promise<boolean> => (await useServerFlagRef(flagName)).value;
